<template>
    <div class="min-h-screen bg-auas-500 staff-profile-page relative overflow-hidden">
        <!-- Animated Background Elements -->
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
            <div
                class="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-400/20 to-primary-600/20 rounded-full blur-3xl animate-float">
            </div>
            <div
                class="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-primary-400/20 to-primary-600/20 rounded-full blur-3xl animate-float-delayed">
            </div>
            <div
                class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-primary-400/10 to-primary-600/10 rounded-full blur-2xl animate-pulse-slow">
            </div>
        </div>

        <!-- Header -->
        <header class="relative z-10 min-h-screen lg:min-h-0  from-auas-500 via-auas-600 to-auas-700 overflow-hidden">
            <!-- Background Pattern -->
            <div class="absolute inset-0 opacity-5">
                <div class="absolute inset-0"
                    style="background-image: radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px); background-size: 60px 60px;">
                </div>
            </div>

            <!-- Floating Orbs -->
            <!-- <div class="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div> -->
            <div
                class="absolute bottom-20 right-10 w-48 h-48 bg-auas-400/10 rounded-full blur-2xl animate-pulse delay-1000">
            </div>

            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                <!-- Back Navigation -->
                <div class="mb-8 lg:mb-12">
                    <Link :href="route('staff.index')"
                        class="inline-flex items-center text-white/70 hover:text-white transition-all duration-300 group">
                        <div
                            class="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mr-3 group-hover:bg-white/20 transition-all duration-300">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                            </svg>
                        </div>
                        <span class="truncate text-white font-medium">Back to Staff Directory</span>
                    </Link>
                </div>

                <!-- Main Header Content -->
                <div class="text-center space-y-8 lg:space-y-12">
                    <!-- Profile Photo -->
                    <div class="inline-flex items-center justify-center">
                        <div class="relative group">
                            <div class="w-48 h-48 lg:w-64 lg:h-64 rounded-full shadow-2xl transition-transform duration-500 group-hover:scale-105 overflow-hidden bg-transparent">
                                <StaffPhoto :src="staff.photo_url" :alt="staff.name_en"
                                    image-class="w-full h-full rounded-full" 
                                    :blurred-background="true" />
                            </div>
                            <!-- Floating Ring Animation -->
                            <!-- <div class="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"></div> -->
                        </div>
                    </div>

                    <!-- Staff Name and Title -->
                    <div class="space-y-4 max-w-4xl mx-auto">
                        <h1 class="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight break-words">
                            {{ staff.name_en }}
                        </h1>
                        <p class="text-xl lg:text-2xl xl:text-3xl text-white font-light break-words">
                            {{ staff.title_en }}
                        </p>
                    </div>

                    <!-- Department and Quick Info -->
                    <div
                        class="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl max-w-4xl mx-auto overflow-hidden">
                        <div class="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-8 text-white">
                            <div
                                class="flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6">
                                    </path>
                                </svg>
                                <span class="truncate text-white font-medium">{{ getStaffDepartmentName() }}</span>
                            </div>
                            <div
                                class="flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6M8 8v10l4-2 4 2V8">
                                    </path>
                                </svg>
                                <span class="truncate text-white font-medium">{{ staff.specialization_en || 'Academic Staff'
                                }}</span>
                            </div>
                            <div v-if="staff.office_location"
                                class="flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z">
                                    </path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                                <span class="truncate text-white font-medium">{{ staff.office_location }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Contact and Action Buttons -->
                    <div
                        class="bg-gradient-to-br from-auas-500/90 to-auas-600/90 backdrop-blur-xl rounded-3xl lg:rounded-[2rem] p-4 lg:p-8 border border-auas-400/30 shadow-2xl max-w-6xl mx-auto overflow-hidden">
                        <!-- Mobile Layout: Responsive Grid -->
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-4">
                            <!-- Contact Details Card -->
                            <div class="sm:col-span-2 bg-white/20 rounded-2xl p-4 border border-white/30">
                                <div class="flex items-center mb-4">
                                    <div
                                        class="w-10 h-10 bg-gradient-to-r from-auas-500 to-auas-600 rounded-xl flex items-center justify-center mr-3">
                                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                                            </path>
                                        </svg>
                                    </div>
                                    <h3 class="text-white font-semibold text-lg">Contact Details</h3>
                                </div>
                                <div class="space-y-3">
                                    <!-- Email -->
                                    <a :href="`mailto:${staff.email}`"
                                        class="flex items-center bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-all duration-300 group">
                                        <svg class="w-5 h-5 text-white mr-3 flex-shrink-0" fill="none"
                                            stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                                            </path>
                                        </svg>
                                        <span class="text-white font-medium text-sm truncate">{{ staff.email }}</span>
                                    </a>

                                    <!-- Phone (if available) -->
                                    <a v-if="staff.phone" :href="`tel:${staff.phone}`"
                                        class="flex items-center bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-all duration-300 group">
                                        <svg class="w-5 h-5 text-white mr-3 flex-shrink-0" fill="none"
                                            stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z">
                                            </path>
                                        </svg>
                                        <span class="text-white font-medium text-sm truncate">{{ staff.phone }}</span>
                                    </a>
                                </div>
                            </div>

                            <!-- AUAS CV Card -->
                            <a :href="route('staff.auascv', staff.id)" target="_blank"
                                class="bg-gradient-to-r from-blue-500/20 to-blue-600/20 hover:from-blue-500/30 hover:to-blue-600/30 rounded-2xl p-4 transition-all duration-300 border border-blue-400/30 hover:border-blue-400/50 group">
                                <div class="flex items-center mb-3">
                                    <div
                                        class="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-3">
                                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                            </path>
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="text-white font-semibold text-sm">AUAS CV</p>
                                        <p class="text-white/70 text-xs">Academic CV</p>
                                    </div>
                                </div>
                            </a>

                            <!-- Download CV Card (if available) -->
                            <a v-if="staff.cv_file" :href="getStaffCvUrl()" target="_blank"
                                class="bg-gradient-to-r from-auas-500/20 to-auas-600/20 hover:from-auas-500/30 hover:to-auas-600/30 rounded-2xl p-4 transition-all duration-300 border border-auas-400/30 hover:border-auas-400/50 group">
                                <div class="flex items-center mb-3">
                                    <div
                                        class="w-10 h-10 bg-gradient-to-r from-auas-500 to-auas-600 rounded-xl flex items-center justify-center mr-3">
                                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                            </path>
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="text-white font-semibold text-sm">Download CV</p>
                                        <p class="text-white/70 text-xs">View PDF</p>
                                    </div>
                                </div>
                            </a>

                            <!-- Share Profile Card -->
                            <button @click="shareProfile"
                                class="bg-gradient-to-r from-auas-500/20 to-auas-600/20 hover:from-auas-500/30 hover:to-auas-600/30 rounded-2xl p-4 transition-all duration-300 border border-auas-400/30 hover:border-auas-400/50 group">
                                <div class="flex items-center mb-3">
                                    <div
                                        class="w-10 h-10 bg-gradient-to-r from-auas-500 to-auas-600 rounded-xl flex items-center justify-center mr-3">
                                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z">
                                            </path>
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="text-white font-semibold text-sm">Share Profile</p>
                                        <p class="text-white/70 text-xs">Social media</p>
                                    </div>
                                </div>
                            </button>
                        </div>

                        <!-- Desktop Layout: Responsive Grid -->
                        <div class="hidden lg:grid gap-6 lg:grid-cols-12">
                            <!-- Contact Details - Takes 6 columns -->
                            <div class="lg:col-span-6 bg-white/20 rounded-2xl p-6 border border-white/30">
                                <div class="flex items-center mb-4">
                                    <div
                                        class="w-12 h-12 bg-gradient-to-r from-auas-500 to-auas-600 rounded-2xl flex items-center justify-center mr-4">
                                        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                                            </path>
                                        </svg>
                                    </div>
                                    <h3 class="text-white font-semibold text-xl">Contact Details</h3>
                                </div>
                                <div class="space-y-3">
                                    <!-- Email -->
                                    <a :href="`mailto:${staff.email}`"
                                        class="flex items-center bg-white/10 hover:bg-white/20 rounded-xl p-4 transition-all duration-300 group">
                                        <svg class="w-5 h-5 text-white mr-4 flex-shrink-0" fill="none"
                                            stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                                            </path>
                                        </svg>
                                        <span class="text-white font-medium text-base truncate">{{ staff.email }}</span>
                                    </a>

                                    <!-- Phone (if available) -->
                                    <a v-if="staff.phone" :href="`tel:${staff.phone}`"
                                        class="flex items-center bg-white/10 hover:bg-white/20 rounded-xl p-4 transition-all duration-300 group">
                                        <svg class="w-5 h-5 text-white mr-4 flex-shrink-0" fill="none"
                                            stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z">
                                            </path>
                                        </svg>
                                        <span class="text-white font-medium text-base truncate">{{ staff.phone }}</span>
                                    </a>

                                </div>
                            </div>

                            <!-- AUAS CV Button - Takes 2 columns -->
                            <a :href="route('staff.auascv', staff.id)" target="_blank"
                                class="lg:col-span-2 bg-gradient-to-r from-blue-500/20 to-blue-600/20 hover:from-blue-500/30 hover:to-blue-600/30 rounded-2xl p-6 transition-all duration-300 border border-blue-400/30 hover:border-blue-400/50 group">
                                <div class="flex flex-col items-center text-center">
                                    <div
                                        class="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4">
                                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                            </path>
                                        </svg>
                                    </div>
                                    <h4 class="text-white font-semibold text-lg mb-1">AUAS CV</h4>
                                    <p class="text-white/70 text-sm">Academic CV</p>
                                </div>
                            </a>

                            <!-- Download CV Button - Takes 2 columns (if available) -->
                            <a v-if="staff.cv_file" :href="getStaffCvUrl()" target="_blank"
                                class="lg:col-span-2 bg-gradient-to-r from-auas-500/20 to-auas-600/20 hover:from-auas-500/30 hover:to-auas-600/30 rounded-2xl p-6 transition-all duration-300 border border-auas-400/30 hover:border-auas-400/50 group">
                                <div class="flex flex-col items-center text-center">
                                    <div
                                        class="w-16 h-16 bg-gradient-to-r from-auas-500 to-auas-600 rounded-2xl flex items-center justify-center mb-4">
                                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                            </path>
                                        </svg>
                                    </div>
                                    <h4 class="text-white font-semibold text-lg mb-1">Download CV</h4>
                                    <p class="text-white/70 text-sm">View PDF Document</p>
                                </div>
                            </a>

                            <!-- Share Profile Button - Takes 2 columns -->
                            <button @click="shareProfile"
                                class="bg-gradient-to-r from-auas-500/20 to-auas-600/20 hover:from-auas-500/30 hover:to-auas-600/30 rounded-2xl p-6 transition-all duration-300 border border-auas-400/30 hover:border-auas-400/50 group"
                                :class="staff.cv_file ? 'lg:col-span-2' : 'lg:col-span-4'">
                                <div class="flex flex-col items-center text-center">
                                    <div
                                        class="w-16 h-16 bg-gradient-to-r from-auas-500 to-auas-600 rounded-2xl flex items-center justify-center mb-4">
                                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z">
                                            </path>
                                        </svg>
                                    </div>
                                    <h4 class="text-white font-semibold text-lg mb-1">Share Profile</h4>
                                    <p class="text-white/70 text-sm">Social media & links</p>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!-- Share Modal -->
        <Teleport to="body">
            <div v-if="showShareModal" class="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-gradient-to-br from-auas-900/90 via-auas-800/80 to-auas-900/90 backdrop-blur-lg backdrop-saturate-150 animate-backdrop-in"
                    @click="closeShareModal"></div>

                <!-- Modal Content -->
                <div class="relative bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 max-w-sm w-full p-6 animate-modal-in"
                    @click.stop>
                    <!-- Close Button -->
                    <button @click="closeShareModal"
                        class="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-300 group backdrop-blur-sm border border-white/30">
                        <svg class="w-4 h-4 text-white group-hover:text-white/80 transition-colors" fill="none"
                            stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>

                    <!-- Header -->
                    <div class="text-center mb-6">
                        <div
                            class="w-10 h-10 bg-gradient-to-r from-auas-500 to-auas-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z">
                                </path>
                            </svg>
                        </div>
                        <h3 class="text-xl font-bold text-white mb-2">Share Profile</h3>
                        <p class="text-white/70 text-base">{{ staff.name_en }}</p>
                        <p class="text-white/50 text-sm mt-1">{{ staff.title_en }}</p>
                    </div>

                    <!-- Share Options -->
                    <div class="space-y-3 mb-6">
                        <button @click="copyToClipboard($event)"
                            class="w-full flex items-center justify-between p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all duration-300 group border border-white/20 backdrop-blur-sm">
                            <div class="flex items-center">
                                <div
                                    class="w-12 h-12 bg-gradient-to-r from-auas-500 to-auas-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z">
                                        </path>
                                    </svg>
                                </div>
                                <div class="text-left">
                                    <p class="font-semibold text-white text-lg">Copy Link</p>
                                    <p class="text-sm text-white/60 ">Copy profile URL to clipboard</p>
                                </div>
                            </div>
                            <svg class="w-5 h-5 text-auas-300 transition-transform group-hover:translate-x-1"
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7">
                                </path>
                            </svg>
                        </button>

                        <button @click="shareViaEmail"
                            class="w-full flex items-center justify-between p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all duration-300 group border border-white/20 backdrop-blur-sm">
                            <div class="flex items-center">
                                <div
                                    class="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                                        </path>
                                    </svg>
                                </div>
                                <div class="text-left">
                                    <p class="font-semibold text-white text-lg">Share via Email</p>
                                    <p class="text-sm text-white/60 ">Send profile link via email</p>
                                </div>
                            </div>
                            <svg class="w-5 h-5 text-auas-300 transition-transform group-hover:translate-x-1"
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7">
                                </path>
                            </svg>
                        </button>

                        <button v-if="canShare" @click="shareViaNative"
                            class="w-full flex items-center justify-between p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all duration-300 group border border-white/20 backdrop-blur-sm">
                            <div class="flex items-center">
                                <div
                                    class="w-12 h-12 bg-gradient-to-r from-auas-500 to-auas-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z">
                                        </path>
                                    </svg>
                                </div>
                                <div class="text-left">
                                    <p class="font-semibold text-white text-lg">Native Share</p>
                                    <p class="text-sm text-white/60 ">Use device share options</p>
                                </div>
                            </div>
                            <svg class="w-5 h-5 text-auas-300 transition-transform group-hover:translate-x-1"
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7">
                                </path>
                            </svg>
                        </button>
                    </div>

                    <!-- URL Preview -->
                    <div class="bg-white/5 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-white/10">
                        <p class="text-xs text-white/60 mb-1">Profile URL:</p>
                        <p class="text-sm text-white/80 font-mono break-all">{{ currentUrl }}</p>
                    </div>

                    <!-- Close Button -->
                    <button @click="closeShareModal"
                        class="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-2xl transition-all duration-300 font-semibold backdrop-blur-sm border border-white/20">
                        Close
                    </button>
                </div>
            </div>
        </Teleport>
        <br><br><br>
        <!-- Main Content Area -->
        <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 lg:-mt-20">
            <!-- Vertical Layout: Sidebar + Content -->
            <div class="flex flex-col lg:flex-row gap-8">
                <!-- Vertical Navigation Sidebar -->
                <div class="lg:w-80 flex-shrink-0">
                    <div class="bg-white rounded-3xl shadow-2xl border border-gray-200 p-6 sticky top-8 overflow-hidden">
                        <h3 class="text-lg font-semibold text-black mb-6 flex items-center">
                            <div
                                class="w-8 h-8 bg-gradient-to-r from-auas-500 to-auas-600 rounded-xl flex items-center justify-center mr-3">
                                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                    </path>
                                </svg>
                            </div>
                            Academic Profile
                        </h3>
                        <nav class="space-y-2">
                            <button v-for="(tab, index) in tabs" :key="tab.id" @click="activeTab = tab.id" :class="[
                                'w-full text-left px-4 py-3 rounded-2xl font-medium text-sm transition-all duration-300 flex items-center group',
                                activeTab === tab.id
                                    ? 'bg-gradient-to-r from-auas-600 to-auas-700 text-white shadow-lg transform scale-105'
                                    : 'text-black hover:text-auas-600 hover:bg-gray-100 hover:transform hover:scale-102'
                            ]" :style="{ animationDelay: `${index * 0.1}s` }" class="animate-fade-in-up">
                                <component :is="getTabIcon(tab.id)" class="w-4 h-4 mr-3 flex-shrink-0" />
                                <span class="flex-1" :class="activeTab === tab.id ? 'text-white' : 'text-black'">{{
                                    tab.name }}</span>
                                <svg v-if="activeTab === tab.id"
                                    class="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" fill="none"
                                    stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 5l7 7-7 7"></path>
                                </svg>
                            </button>
                        </nav>

                        <!-- Quick Stats -->
                        <div class="mt-8 p-4 bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
                            <h4 class="text-sm font-semibold text-black mb-3">Quick Overview</h4>
                            <div class="space-y-2 text-xs text-black">
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Publications:</span>
                                    <span class="font-medium text-auas-600">{{ publications.length || 0 }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Teaching Subjects:</span>
                                    <span class="font-medium text-auas-600">{{ staff.teaching_subjects?.length || 0
                                    }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Academic Activities:</span>
                                    <span class="font-medium text-auas-600">{{ (staff.academic_conferences?.length || 0)
                                        + (staff.academic_workshops?.length || 0) + (staff.academic_trainings?.length ||
                                            0) }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Supervisions:</span>
                                    <span class="font-medium text-auas-600">{{ staff.supervisions?.length || 0 }}</span>
                                </div>
                            </div>
                        </div>


                    </div>
                </div>

                <!-- Main Content Area -->
                <div class="flex-1">
                    <div class="bg-white rounded-3xl shadow-2xl border border-gray-200 p-8 min-h-[600px] overflow-hidden">
                        <!-- Content Header -->
                        <div class="flex items-center justify-between mb-8">
                            <div>
                                <h2 class="text-2xl font-bold text-black">
                                    {{ getCurrentTabName() }}
                                </h2>
                                <p class="text-gray-600 mt-1">{{ getCurrentTabDescription() }}</p>
                            </div>
                            <!-- <div class="flex items-center space-x-2">
                                <div class="w-2 h-2 bg-auas-400 rounded-full animate-pulse"></div>
                                <span class="text-sm text-white/60 ">Live Data</span>
                            </div> -->
                        </div>

                        <!-- Tab Content -->
                        <div class="animate-fade-in">
                            <!-- Personal Information Tab -->
                            <div v-if="activeTab === 'personal_info'">
                                <h3 class="text-2xl font-bold text-black mb-6">Personal Information</h3>

                                <div class="bg-gray-50 border border-gray-200 rounded-2xl p-6 overflow-hidden">
                                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <!-- Email -->
                                        <div class="bg-white rounded-xl p-4 border border-gray-200 overflow-hidden">
                                            <p class="text-xs text-gray-600 mb-1">Email</p>
                                            <p class="text-sm font-medium text-black break-words">{{ staff.email }}</p>
                                        </div>

                                        <!-- Phone -->
                                        <div v-if="staff.phone" class="bg-white rounded-xl p-4 border border-gray-200 overflow-hidden">
                                            <p class="text-xs text-gray-600 mb-1">Phone</p>
                                            <p class="text-sm font-medium text-black break-words">{{ staff.phone }}</p>
                                        </div>

                                        <!-- Gender -->
                                        <div v-if="staff.gender" class="bg-white rounded-xl p-4 border border-gray-200 overflow-hidden">
                                            <p class="text-xs text-gray-600 mb-1">Gender</p>
                                            <p class="text-sm font-medium text-black break-words">{{ staff.gender }}</p>
                                        </div>

                                        <!-- Date of Birth -->
                                        <div v-if="staff.date_of_birth"
                                            class="bg-white rounded-xl p-4 border border-gray-200 overflow-hidden">
                                            <p class="text-xs text-gray-600 mb-1">Date of Birth</p>
                                            <p class="text-sm font-medium text-black break-words">{{ new
                                                Date(staff.date_of_birth).toLocaleDateString('en-GB', {
                                                    year: 'numeric',
                                                    month: 'long', day: 'numeric'
                                                }) }}</p>
                                        </div>

                                        <!-- Nationality -->
                                        <div v-if="staff.nationality"
                                            class="bg-white rounded-xl p-4 border border-gray-200 overflow-hidden">
                                            <p class="text-xs text-gray-600 mb-1">Nationality</p>
                                            <p class="text-sm font-medium text-black break-words">{{ staff.nationality }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Biography Tab -->
                            <div v-if="activeTab === 'biography'">
                                <h3 class="text-2xl font-bold text-black mb-6">Biography</h3>

                                <!-- Biography -->
                                <div class="bg-gray-50 rounded-2xl p-6 border border-gray-200 overflow-hidden">
                                    <h4 class="text-lg font-semibold text-black mb-4 flex items-center">
                                        <svg class="w-5 h-5 text-auas-600 mr-2" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                            </path>
                                        </svg>
                                        Professional Biography
                                    </h4>
                                    <div v-if="staff.biography_en" class="max-w-none text-black break-words"
                                        v-html="staff.biography_en"></div>
                                    <p v-else class="text-gray-600 italic">No biography available.</p>
                                </div>
                            </div>

                            <!-- Education Tab -->
                            <div v-if="activeTab === 'education'">
                                <h3 class="text-2xl font-bold text-black mb-4">Education</h3>
                                <div v-if="staff.education && staff.education.length > 0" class="space-y-6">
                                    <div v-for="(edu, index) in staff.education" :key="index"
                                        class="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                                        <div class="flex items-start space-x-4">
                                            <div class="flex-shrink-0">
                                                <div
                                                    class="w-12 h-12 bg-gradient-to-br from-auas-500 to-auas-600 rounded-2xl flex items-center justify-center">
                                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5z"></path>
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z">
                                                        </path>
                                                    </svg>
                                                </div>
                                            </div>
                                            <div class="flex-1">
                                                <h4 class="text-xl font-bold text-black mb-2 font-zain break-words">{{ edu.degree }}</h4>
                                                <p class="text-lg text-auas-600 font-semibold mb-1 font-zain break-words">{{ edu.university ||
                                                    edu.institution }}</p>
                                                <div class="flex items-center space-x-4 text-sm text-gray-600">
                                                    <span class="flex items-center">
                                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                                            </path>
                                                        </svg>
                                                        <!-- Show year range if both start and end years exist -->
                                                        <template v-if="edu.start_year && edu.end_year">
                                                            {{ edu.start_year }} - {{ edu.end_year }}
                                                        </template>
                                                        <template v-else-if="edu.start_year">
                                                            {{ edu.start_year }} - Present
                                                        </template>
                                                        <template v-else-if="edu.end_year">
                                                            {{ edu.end_year }}
                                                        </template>
                                                        <template v-else-if="edu.year">
                                                            {{ edu.year }}
                                                        </template>
                                                        <template v-else>
                                                            Year not specified
                                                        </template>
                                                    </span>
                                                    <span v-if="edu.country" class="flex items-center">
                                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z">
                                                            </path>
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z">
                                                            </path>
                                                        </svg>
                                                        {{ edu.country }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p v-else class="text-gray-600 italic text-center py-8">No education information
                                    available.</p>
                            </div>

                            <!-- Scientific Titles Tab -->
                            <div v-if="activeTab === 'scientific_titles'">
                                <h3 class="text-2xl font-bold text-black mb-4">Scientific Titles</h3>
                                <div v-if="staff.scientific_titles && staff.scientific_titles.length > 0"
                                    class="space-y-6">
                                    <div v-for="(title, index) in staff.scientific_titles" :key="index"
                                        class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                                        <div class="flex items-start space-x-4">
                                            <div class="flex-shrink-0">
                                                <div
                                                    class="w-12 h-12 bg-gradient-to-br from-auas-500 to-auas-600 rounded-2xl flex items-center justify-center">
                                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z">
                                                        </path>
                                                    </svg>
                                                </div>
                                            </div>
                                            <div class="flex-1">
                                                <h4 class="text-xl font-bold text-black mb-2 break-words">{{ title.title }}</h4>
                                                <p v-if="title.institution"
                                                    class="text-lg text-auas-600 font-semibold mb-1 break-words">{{
                                                        title.institution }}
                                                </p>
                                                <div class="flex items-center space-x-4 text-sm text-gray-600">
                                                    <span v-if="title.year" class="flex items-center">
                                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2">
                                                            </path>
                                                        </svg>
                                                        {{ title.year }}
                                                    </span>
                                                    <span v-if="title.field" class="flex items-center">
                                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10">
                                                            </path>
                                                        </svg>
                                                        {{ title.field }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p v-else class="text-gray-600 italic text-center py-8">No scientific titles available.
                                </p>
                            </div>

                            <!-- Teaching Tab -->
                            <div v-if="activeTab === 'teaching'" id="teaching-subjects">
                                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                                    <h3 class="text-2xl font-bold text-black">Teaching Subjects</h3>
                                    <!-- Per Page Selector -->
                                    <div v-if="staff.teaching_subjects && staff.teaching_subjects.length > 5"
                                        class="flex items-center space-x-2">
                                        <label class="text-sm font-medium text-gray-600">Show:</label>
                                        <select v-model="teachingPerPage"
                                            @change="changeTeachingPerPage(teachingPerPage)"
                                            class="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-black focus:outline-none focus:ring-2 focus:ring-auas-300 focus:border-transparent [&>option]:text-black [&>option]:bg-white">
                                            <option v-for="option in teachingPerPageOptions" :key="option"
                                                :value="option" class="text-black bg-white">{{ option }} per page
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div v-if="staff.teaching_subjects && staff.teaching_subjects.length > 0"
                                    class="space-y-4">
                                    <div v-for="(subject, index) in paginatedTeachingSubjects" :key="index"
                                        class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                                        <div class="flex items-start space-x-4">
                                            <div class="flex-shrink-0">
                                                <div
                                                    class="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center">
                                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253">
                                                        </path>
                                                    </svg>
                                                </div>
                                            </div>
                                            <div class="flex-1">
                                                <h4 class="text-lg font-bold text-black mb-2 font-zain break-words">{{ subject.subject }}</h4>
                                                <div
                                                    class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 text-sm text-gray-600">
                                                    <div v-if="subject.college_institute" class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-400" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Type:</span>
                                                        <span class="ml-1 capitalize">{{ subject.college_institute
                                                        }}</span>
                                                    </div>
                                                    <div v-if="subject.semester" class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-400" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Semester:</span>
                                                        <span class="ml-1">{{ subject.semester }}</span>
                                                    </div>
                                                    <div v-if="subject.year" class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-400" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Year:</span>
                                                        <span class="ml-1">{{ subject.year }}</span>
                                                    </div>
                                                    <div v-if="subject.level" class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-400" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422A12.083 12.083 0 0112 20.055a12.083 12.083 0 01-6.16-9.477L12 14z">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Level:</span>
                                                        <span class="ml-1 capitalize">{{ subject.level }}</span>
                                                    </div>
                                                    <div v-if="subject.hours" class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-400" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Hours:</span>
                                                        <span class="ml-1">{{ subject.hours }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Pagination Controls -->
                                    <div v-if="teachingTotalPages > 1"
                                        class="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-gray-200">
                                        <div class="text-sm text-gray-600">
                                            Showing {{ (teachingCurrentPage - 1) * teachingPerPage + 1 }} - {{
                                                Math.min(teachingCurrentPage *
                                                    teachingPerPage, staff.teaching_subjects.length) }} of {{
                                                staff.teaching_subjects.length }} subjects
                                        </div>
                                        <div class="flex items-center space-x-2">
                                            <button @click="changeTeachingPage(teachingCurrentPage - 1)"
                                                :disabled="teachingCurrentPage === 1"
                                                class="px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 19l-7-7 7-7">
                                                    </path>
                                                </svg>
                                            </button>
                                            <template v-for="page in teachingTotalPages" :key="page">
                                                <button
                                                    v-if="page === 1 || page === teachingTotalPages || (page >= teachingCurrentPage - 1 && page <= teachingCurrentPage + 1)"
                                                    @click="changeTeachingPage(page)" :class="[
                                                        'px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                                                        page === teachingCurrentPage
                                                            ? 'bg-gradient-to-r from-auas-500 to-auas-600 text-white shadow-lg'
                                                            : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'
                                                    ]">
                                                    {{ page }}
                                                </button>
                                                <span
                                                    v-else-if="page === teachingCurrentPage - 2 || page === teachingCurrentPage + 2"
                                                    class="px-1 text-gray-400">...</span>
                                            </template>
                                            <button @click="changeTeachingPage(teachingCurrentPage + 1)"
                                                :disabled="teachingCurrentPage === teachingTotalPages"
                                                class="px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M9 5l7 7-7 7"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <p v-else class="text-gray-600 italic text-center py-8">No teaching information
                                    available.</p>
                            </div>

                            <!-- Research Tab -->
                            <div v-if="activeTab === 'research'" id="research-publications">
                                <div class="flex items-center justify-between mb-6">
                                    <h3 class="text-2xl font-bold text-black">Research Publications</h3>
                                    <a v-if="staff.google_scholar_url" :href="staff.google_scholar_url" target="_blank"
                                        class="text-sm text-auas-600 hover:text-auas-700 flex items-center gap-2">
                                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path
                                                d="M12 24a7 7 0 110-14 7 7 0 010 14zm0-24L0 9.5l4.838 3.94A8 8 0 0112 9a8 8 0 017.162 4.44L24 9.5z" />
                                        </svg>
                                        View on Google Scholar
                                    </a>
                                    <a v-if="orcidProfile && orcidProfile.url" :href="orcidProfile.url" target="_blank"
                                        class="text-sm text-auas-600 hover:text-auas-700 flex items-center gap-2">
                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                        </svg>
                                        View on ORCID
                                    </a>
                                </div>

                                <!-- Loading State -->
                                <div v-if="loadingPublications" class="flex justify-center py-12">
                                    <div
                                        class="animate-spin rounded-full h-12 w-12 border-4 border-auas-500 border-t-transparent">
                                    </div>
                                </div>

                                <!-- Error State -->
                                <div v-else-if="publicationsError"
                                    class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                                    <p class="text-red-600">{{ publicationsError }}</p>
                                    <button @click="fetchPublications"
                                        class="mt-4 px-4 py-2 bg-auas-600 text-white rounded-md hover:bg-auas-700">
                                        Try Again
                                    </button>
                                </div>

                                <!-- Publications List with Pagination -->
                                <div v-else-if="publications && publications.length > 0">
                                    <div v-if="publicationsMessage"
                                        class="mb-4 rounded-lg border border-auas-100 bg-auas-50 px-4 py-3 text-sm text-auas-700">
                                        {{ publicationsMessage }}
                                    </div>
                                    <!-- Mobile-First Controls -->
                                    <div class="mb-6">
                                        <!-- Mobile Layout -->
                                        <div class="block sm:hidden">
                                            <!-- Total Count -->
                                            <div class="bg-white border border-gray-200 rounded-xl p-4 mb-4 overflow-hidden">
                                                <div class="text-center">
                                                    <h4 class="text-lg font-semibold text-black mb-1">{{
                                                        publications.length }} Publications</h4>
                                                    <p class="text-sm text-gray-600">Research contributions</p>
                                                </div>
                                            </div>

                                            <!-- Per Page Selector -->
                                            <div class="bg-white border border-gray-200 rounded-xl p-4 mb-4 overflow-hidden">
                                                <label class="block text-sm font-medium text-black mb-2">Show per
                                                    page:</label>
                                                <select v-model="perPage" @change="changePerPage(perPage)"
                                                    class="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-auas-500 focus:border-transparent bg-white text-black">
                                                    <option v-for="option in perPageOptions" :key="option"
                                                        :value="option">{{ option }} per page
                                                    </option>
                                                </select>
                                            </div>

                                            <!-- Current Range -->
                                            <div class="text-center text-sm text-gray-600 mb-4">
                                                Showing {{ (currentPage - 1) * perPage + 1 }} to {{ Math.min(currentPage
                                                    * perPage,
                                                    publications.length) }} of {{ publications.length }}
                                            </div>
                                        </div>

                                        <!-- Desktop Layout -->
                                        <div
                                            class="hidden sm:flex items-center justify-between bg-white border border-gray-200 p-4 rounded-lg overflow-hidden">
                                            <div class="flex items-center gap-4">
                                                <span class="text-sm text-black font-medium">Total: {{
                                                    publications.length }} publications</span>
                                                <div class="flex items-center gap-2">
                                                    <label for="perPage" class="text-sm text-black">Show per
                                                        page:</label>
                                                    <select id="perPage" v-model="perPage"
                                                        @change="changePerPage(perPage)"
                                                        class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-auas-500 bg-white text-black">
                                                        <option v-for="option in perPageOptions" :key="option"
                                                            :value="option">{{ option }}</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <span class="text-sm text-gray-600">
                                                Showing {{ (currentPage - 1) * perPage + 1 }} to {{ Math.min(currentPage
                                                    * perPage,
                                                    publications.length) }}
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Publications -->
                                    <div class="space-y-4 mb-6">
                                        <div v-for="(pub, index) in paginatedPublications" :key="index"
                                            class="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
                                            <!-- Mobile Layout -->
                                            <div class="block sm:hidden p-4">
                                                <!-- Header with Year and Citations -->
                                                <div class="flex items-center justify-between mb-3">
                                                    <div
                                                        class="bg-gradient-to-r from-auas-500 to-auas-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-sm">
                                                        {{ pub.year }}
                                                    </div>
                                                    <div class="text-right">
                                                        <div class="text-xs text-white font-medium">CITED BY</div>
                                                        <div class="text-sm font-bold text-auas-600">{{ pub.citations }}
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Title -->
                                                <h4 class="text-base font-bold text-black mb-3 leading-tight break-words">
                                                    <a v-if="pub.link" :href="pub.link" target="_blank"
                                                        class="hover:text-auas-600 transition-colors">
                                                        {{ pub.title }}
                                                    </a>
                                                    <span v-else>{{ pub.title }}</span>
                                                </h4>

                                                <!-- Authors and Publication -->
                                                <div class="space-y-2">
                                                    <div v-if="pub.authors" class="text-sm text-gray-600">
                                                        <span class="font-medium">Authors:</span> {{ pub.authors }}
                                                    </div>
                                                    <div v-if="pub.publication" class="text-sm text-gray-600">
                                                        <span class="font-medium">Journal:</span> {{ pub.publication }}
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Desktop Layout -->
                                            <div class="hidden sm:block p-6">
                                                <div class="flex items-start justify-between gap-4">
                                                    <div class="flex-1">
                                                        <h4 class="text-lg font-bold text-black mb-2 break-words">
                                                            <a v-if="pub.link" :href="pub.link" target="_blank"
                                                                class="hover:text-auas-600 transition-colors">
                                                                {{ pub.title }}
                                                            </a>
                                                            <span v-else>{{ pub.title }}</span>
                                                        </h4>
                                                        <p v-if="pub.authors" class="text-sm text-gray-600 mb-1">{{
                                                            pub.authors }}</p>
                                                        <p v-if="pub.publication" class="text-sm text-gray-600 italic">
                                                            {{ pub.publication }}</p>
                                                    </div>
                                                    <div class="flex flex-col items-end gap-3 flex-shrink-0">
                                                        <div v-if="pub.year"
                                                            class="bg-gradient-to-r from-auas-500 to-auas-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-sm">
                                                            {{ pub.year }}
                                                        </div>
                                                        <div v-if="pub.citations > 0"
                                                            class="bg-gradient-to-r from-auas-50 to-auas-100 border border-auas-200 rounded-lg p-1.5 text-center min-w-[60px]">
                                                            <div class="text-xs text-auas-600 font-semibold mb-0.5">
                                                                CITED BY</div>
                                                            <div class="text-sm font-bold text-auas-700">{{
                                                                pub.citations }}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Pagination Controls -->
                                    <div v-if="totalPages > 1" class="mt-6">
                                        <!-- Mobile Pagination -->
                                        <div class="block sm:hidden">
                                            <div class="flex items-center justify-between mb-4">
                                                <button @click="changePage(currentPage - 1)"
                                                    :disabled="currentPage === 1"
                                                    class="flex items-center gap-2 px-4 py-3 bg-auas-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400">
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2" d="M15 19l-7-7 7-7">
                                                        </path>
                                                    </svg>
                                                    Previous
                                                </button>

                                                <div class="text-center">
                                                    <div class="text-sm text-white">Page</div>
                                                    <div class="text-lg font-bold text-white">{{ currentPage }} of {{
                                                        totalPages }}</div>
                                                </div>

                                                <button @click="changePage(currentPage + 1)"
                                                    :disabled="currentPage === totalPages"
                                                    class="flex items-center gap-2 px-4 py-3 bg-auas-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400">
                                                    Next
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2" d="M9 5l7 7-7 7">
                                                        </path>
                                                    </svg>
                                                </button>
                                            </div>

                                            <!-- Page Numbers for Mobile -->
                                            <div class="flex items-center justify-center gap-1">
                                                <template v-for="page in totalPages" :key="page">
                                                    <button
                                                        v-if="page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)"
                                                        @click="changePage(page)" :class="[
                                                            'w-10 h-10 rounded-lg text-sm font-medium',
                                                            page === currentPage
                                                                ? 'bg-auas-600 text-white'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        ]">
                                                        {{ page }}
                                                    </button>
                                                    <span
                                                        v-else-if="page === currentPage - 2 || page === currentPage + 2"
                                                        class="px-2 text-gray-400">...</span>
                                                </template>
                                            </div>
                                        </div>

                                        <!-- Desktop Pagination -->
                                        <div class="hidden sm:flex items-center justify-center gap-2">
                                            <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1"
                                                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                                Previous
                                            </button>

                                            <template v-for="page in totalPages" :key="page">
                                                <button
                                                    v-if="page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)"
                                                    @click="changePage(page)" :class="[
                                                        'px-4 py-2 border rounded-md text-sm font-medium',
                                                        page === currentPage
                                                            ? 'bg-auas-600 text-white border-auas-600'
                                                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                                                    ]">
                                                    {{ page }}
                                                </button>
                                                <span v-else-if="page === currentPage - 3 || page === currentPage + 3"
                                                    class="px-2 text-gray-500">...</span>
                                            </template>

                                            <button @click="changePage(currentPage + 1)"
                                                :disabled="currentPage === totalPages"
                                                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- No Publications -->
                                <div v-else class="text-center py-12">
                                    <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p class="text-gray-500">
                                        {{ publicationsMessage || 'No research publications configured for this staff member.' }}
                                    </p>
                                    <button v-if="hasExternalPublicationSource" @click="fetchPublications(true)"
                                        class="mt-4 px-4 py-2 bg-auas-600 text-white rounded-md hover:bg-auas-700">
                                        Reload publications
                                    </button>
                                </div>
                            </div>


                            <!-- Academic Activities Tab -->
                            <div v-if="activeTab === 'academic_activities'">
                                <h3 class="text-2xl font-bold text-black mb-6">Academic Activities</h3>

                                <!-- Activity Summary Cards -->
                                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                    <!-- Conferences Card -->
                                    <div class="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                                        @click="toggleSection('conferences')">
                                        <div class="flex items-center justify-between mb-4">
                                            <div class="flex items-center">
                                                <div
                                                    class="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                                                        </path>
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h4 class="text-lg font-semibold text-black">Conferences</h4>
                                                    <p class="text-sm text-gray-600">{{
                                                        staff.academic_conferences?.length || 0 }} entries</p>
                                                </div>
                                            </div>
                                            <svg class="w-5 h-5 text-gray-600 transition-transform duration-300"
                                                :class="{ 'rotate-180': expandedSections.conferences }" fill="none"
                                                stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                        </div>
                                    </div>

                                    <!-- Workshops Card -->
                                    <div class="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                                        @click="toggleSection('workshops')">
                                        <div class="flex items-center justify-between mb-4">
                                            <div class="flex items-center">
                                                <div
                                                    class="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10">
                                                        </path>
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h4 class="text-lg font-semibold text-black">Workshops</h4>
                                                    <p class="text-sm text-gray-600">{{ staff.academic_workshops?.length
                                                        || 0 }} entries</p>
                                                </div>
                                            </div>
                                            <svg class="w-5 h-5 text-gray-600 transition-transform duration-300"
                                                :class="{ 'rotate-180': expandedSections.workshops }" fill="none"
                                                stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                        </div>
                                    </div>

                                    <!-- Trainings Card -->
                                    <div class="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                                        @click="toggleSection('trainings')">
                                        <div class="flex items-center justify-between mb-4">
                                            <div class="flex items-center">
                                                <div
                                                    class="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5z"></path>
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z">
                                                        </path>
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h4 class="text-lg font-semibold text-black">Trainings</h4>
                                                    <p class="text-sm text-gray-600">{{ staff.academic_trainings?.length
                                                        || 0 }} entries</p>
                                                </div>
                                            </div>
                                            <svg class="w-5 h-5 text-gray-600 transition-transform duration-300"
                                                :class="{ 'rotate-180': expandedSections.trainings }" fill="none"
                                                stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <!-- Collapsible Sections -->
                                <!-- Conferences Section -->
                                <div v-if="expandedSections.conferences" class="mb-8">
                                    <h4 class="text-xl font-semibold text-black mb-4 flex items-center">
                                        <div
                                            class="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-3">
                                            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                                                </path>
                                            </svg>
                                        </div>
                                        Conferences
                                    </h4>
                                    <div v-if="staff.academic_conferences && staff.academic_conferences.length > 0"
                                        class="space-y-4">
                                        <div v-for="(conference, index) in staff.academic_conferences" :key="index"
                                            class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                                            <div class="flex items-start space-x-4">
                                                <div class="flex-shrink-0">
                                                    <div
                                                        class="w-10 h-10 bg-gradient-to-br from-auas-500 to-auas-600 rounded-xl flex items-center justify-center">
                                                        <svg class="w-5 h-5 text-white" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
                                                            </path>
                                                        </svg>
                                                    </div>
                                                </div>
                                                <div class="flex-1">
                                                    <h5 class="text-xl text-auas-600 font-bold mb-3 capitalize break-words">{{
                                                        conference.conference_title || 'Untitled Conference' }}
                                                    </h5>
                                                    <div class="flex flex-wrap gap-x-8 gap-y-4 text-sm text-gray-600">
                                                        <div class="flex items-start min-w-[150px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-500 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Location:</span>
                                                                <span class="ml-1">{{ conference.location || 'N/A'
                                                                    }}</span>
                                                            </div>
                                                        </div>
                                                        <div class="flex items-start min-w-[120px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-500 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Year:</span>
                                                                <span class="ml-1">{{ conference.year || 'N/A' }}</span>
                                                            </div>
                                                        </div>
                                                        <div v-if="conference.role"
                                                            class="flex items-start min-w-[150px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-500 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Role:</span>
                                                                <span class="ml-1">{{ conference.role }}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <p v-else class="text-gray-600 italic text-center py-4">No conference participation
                                        recorded.</p>
                                </div>

                                <!-- Workshops Section -->
                                <div v-if="expandedSections.workshops" class="mb-8">
                                    <h4 class="text-xl font-semibold text-black mb-4 flex items-center">
                                        <div
                                            class="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-3">
                                            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10">
                                                </path>
                                            </svg>
                                        </div>
                                        Workshops
                                    </h4>
                                    <div v-if="staff.academic_workshops && staff.academic_workshops.length > 0"
                                        class="space-y-4">
                                        <div v-for="(workshop, index) in staff.academic_workshops" :key="index"
                                            class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                                            <div class="flex items-start space-x-4">
                                                <div class="flex-shrink-0">
                                                    <div
                                                        class="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                                                        <svg class="w-5 h-5 text-white" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253">
                                                            </path>
                                                        </svg>
                                                    </div>
                                                </div>
                                                <div class="flex-1">
                                                    <h5 class="text-xl text-auas-600 font-bold mb-3 capitalize break-words">{{
                                                        workshop.workshop_title || 'Untitled Workshop' }}
                                                    </h5>
                                                    <div class="flex flex-wrap gap-x-8 gap-y-4 text-sm text-gray-600">
                                                        <div class="flex items-start min-w-[150px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-300 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Location:</span>
                                                                <span class="ml-1">{{ workshop.location || 'N/A'
                                                                    }}</span>
                                                            </div>
                                                        </div>
                                                        <div class="flex items-start min-w-[120px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-300 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Year:</span>
                                                                <span class="ml-1">{{ workshop.year || 'N/A' }}</span>
                                                            </div>
                                                        </div>
                                                        <div v-if="workshop.role"
                                                            class="flex items-start min-w-[150px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-300 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Role:</span>
                                                                <span class="ml-1">{{ workshop.role }}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <p v-else class="text-gray-600 italic text-center py-4">No workshop participation
                                        recorded.</p>
                                </div>

                                <!-- Trainings Section -->
                                <div v-if="expandedSections.trainings" class="mb-8">
                                    <h4 class="text-xl font-semibold text-black mb-4 flex items-center">
                                        <div
                                            class="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                                            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 14l9-5-9-5-9 5 9 5z">
                                                </path>
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z">
                                                </path>
                                            </svg>
                                        </div>
                                        Trainings
                                    </h4>
                                    <div v-if="staff.academic_trainings && staff.academic_trainings.length > 0"
                                        class="space-y-4">
                                        <div v-for="(training, index) in staff.academic_trainings" :key="index"
                                            class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                                            <div class="flex items-start space-x-4">
                                                <div class="flex-shrink-0">
                                                    <div
                                                        class="w-10 h-10 bg-gradient-to-br from-auas-500 to-auas-600 rounded-xl flex items-center justify-center">
                                                        <svg class="w-5 h-5 text-white" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z">
                                                            </path>
                                                        </svg>
                                                    </div>
                                                </div>
                                                <div class="flex-1">
                                                    <h5 class="text-xl text-auas-600 font-bold mb-3 capitalize break-words">{{
                                                        training.training_title || 'Untitled Training' }}
                                                    </h5>
                                                    <div class="flex flex-wrap gap-x-8 gap-y-4 text-sm text-gray-600">
                                                        <div class="flex items-start min-w-[150px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-300 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Provider:</span>
                                                                <span class="ml-1">{{ training.provider || 'N/A'
                                                                    }}</span>
                                                            </div>
                                                        </div>
                                                        <div class="flex items-start min-w-[120px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-300 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Year:</span>
                                                                <span class="ml-1">{{ training.year || 'N/A' }}</span>
                                                            </div>
                                                        </div>
                                                        <div v-if="training.duration"
                                                            class="flex items-start min-w-[150px]">
                                                            <svg class="w-4 h-4 mr-2 mt-1 text-auas-300 flex-shrink-0"
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z">
                                                                </path>
                                                            </svg>
                                                            <div>
                                                                <span class="font-bold text-gray-700">Duration:</span>
                                                                <span class="ml-1">{{ training.duration }}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <p v-else class="text-gray-600 italic text-center py-4">No training records
                                        available.</p>
                                </div>
                            </div>

                            <!-- Supervision Tab -->
                            <div v-if="activeTab === 'supervision'">
                                <div class="flex flex-col sm:flex-row justify-between items-center mb-6">
                                    <h3 class="text-2xl font-bold text-black">Academic Supervising</h3>
                                    <!-- Items per page control moved to header -->
                                    <div v-if="staff.supervisions && staff.supervisions.length > 0"
                                        class="flex items-center space-x-2 text-sm text-gray-600 mt-4 sm:mt-0">
                                        <span>Show</span>
                                        <select v-model="supervisionPerPage"
                                            @change="changeSupervisionPerPage($event.target.value)"
                                            class="bg-white border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-auas-500 focus:border-auas-500 block w-16 p-2">
                                            <option v-for="option in supervisionPerPageOptions" :key="option"
                                                :value="option">
                                                {{ option }}
                                            </option>
                                        </select>
                                        <span>entries</span>
                                    </div>
                                </div>
                                <div v-if="staff.supervisions && staff.supervisions.length > 0" class="space-y-6">
                                    <div v-for="(supervision, index) in paginatedSupervisions" :key="index"
                                        class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                                        <div class="flex items-start space-x-4">
                                            <div class="flex-shrink-0">
                                                <div
                                                    class="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z">
                                                        </path>
                                                    </svg>
                                                </div>
                                            </div>
                                            <div class="flex-1">
                                                <h4 class="text-xl font-bold text-black mb-2 break-words">{{
                                                    supervision.student_name }}</h4>
                                                <p class="text-lg text-auas-600 font-semibold mb-3 break-words">{{
                                                    supervision.thesis_title }}</p>
                                                <div
                                                    class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                    <div class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-300" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Degree Level:</span>
                                                        <span class="ml-1">{{ supervision.degree_level || 'N/A'
                                                            }}</span>
                                                    </div>
                                                    <div class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-300" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Year:</span>
                                                        <span
                                                            class="ml-1 bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">{{
                                                                supervision.year || 'N/A' }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Pagination Controls -->
                                    <div v-if="staff.supervisions.length > 0"
                                        class="flex flex-col sm:flex-row justify-end items-center mt-6 gap-4 bg-gray-50 rounded-xl p-4">


                                        <!-- Page Navigation -->
                                        <div class="flex items-center space-x-2">
                                            <button
                                                @click="supervisionCurrentPage > 1 && changeSupervisionPage(supervisionCurrentPage - 1)"
                                                :disabled="supervisionCurrentPage === 1"
                                                class="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 19l-7-7 7-7">
                                                    </path>
                                                </svg>
                                            </button>

                                            <span class="text-sm font-medium text-gray-700">
                                                Page {{ supervisionCurrentPage }} of {{ supervisionTotalPages }}
                                            </span>

                                            <button
                                                @click="supervisionCurrentPage < supervisionTotalPages && changeSupervisionPage(supervisionCurrentPage + 1)"
                                                :disabled="supervisionCurrentPage === supervisionTotalPages"
                                                class="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M9 5l7 7-7 7"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <p v-else class="text-gray-600 italic text-center py-8">No supervision information
                                    available.</p>
                            </div>

                            <!-- Memberships Tab -->
                            <div v-if="activeTab === 'memberships'">
                                <h3 class="text-2xl font-bold text-black mb-4">Memberships</h3>
                                <div v-if="staff.memberships && staff.memberships.length > 0" class="space-y-6">
                                    <div v-for="(membership, index) in staff.memberships" :key="index"
                                        class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                                        <div class="flex items-start space-x-4">
                                            <div class="flex-shrink-0">
                                                <div
                                                    class="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
                                                        </path>
                                                    </svg>
                                                </div>
                                            </div>
                                            <div class="flex-1">
                                                <h4 class="text-xl font-bold text-black mb-3 break-words">{{ membership.organization
                                                }}</h4>
                                                <div
                                                    class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                    <div class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-300" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6.5">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Role:</span>
                                                        <span class="ml-1">{{ membership.role || 'Not specified'
                                                        }}</span>
                                                    </div>
                                                    <div class="flex items-center">
                                                        <svg class="w-4 h-4 mr-2 text-auas-300" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                                            </path>
                                                        </svg>
                                                        <span class="font-medium">Year:</span>
                                                        <span
                                                            class="ml-1 bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">{{
                                                                membership.year || 'N/A' }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p v-else class="text-gray-600 italic text-center py-8">No membership information
                                    available.</p>
                            </div>

                            <!-- Academic Profiles Tab -->
                            <div v-if="activeTab === 'academic_profiles'">
                                <h3 class="text-2xl font-bold text-black mb-6">Academic Platforms</h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <!-- Use staff.academic_platforms from the model -->
                                    <component :is="platform.safeUrl ? 'a' : 'div'"
                                        v-for="(platform, index) in safeAcademicPlatforms" :key="index"
                                        :href="platform.safeUrl || undefined" :target="platform.safeUrl ? '_blank' : undefined"
                                        :rel="platform.safeUrl ? 'noopener noreferrer' : undefined"
                                        class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 group overflow-hidden">
                                        <div class="flex items-center space-x-4">
                                            <div
                                                class="w-12 h-12 bg-white border border-gray-200 rounded-2xl flex items-center justify-center group-hover:border-auas-300 transition-colors">
                                                <!-- Google Scholar Icon -->
                                                <svg v-if="platform.icon === 'scholar'"
                                                    class="w-6 h-6 text-gray-700 group-hover:text-auas-600"
                                                    fill="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        d="M12 24a7 7 0 110-14 7 7 0 010 14zm0-24L0 9.5l4.838 3.94A8 8 0 0112 9a8 8 0 017.162 4.44L24 9.5z" />
                                                </svg>
                                                <!-- ResearchGate Icon -->
                                                <svg v-else-if="platform.icon === 'researchgate'"
                                                    class="w-6 h-6 text-[#00ccbb]" fill="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path
                                                        d="M19.586 0c-8.18 0-15.033 6.675-15.033 14.125 0 2.946 1.13 5.926 3.12 8.78h-2.16v1.095h9.91v-1.095h-2.18c-1.57-2.61-2.48-5.35-2.48-7.98 0-6.42 5.09-11.97 12.35-11.97 4.09 0 6.94 2.27 6.94 5.22 0 3.39-3.48 4.67-6.23 4.67-2.25 0-3.69-1.34-3.69-3.08 0-1.74 1.34-3.55 3.32-3.55 1.57 0 2.58.91 2.58 2.22 0 1.94-2.88 2.11-2.88-1.54 0-1.04-.6-1.51-1.37-1.51-1.01 0-1.51.91-1.51 2.15 0 2.78 2.82 3.69 5.26 3.69 3.08 0 6.67-1.41 6.67-5.96 0-3.99-3.51-5.26-6.67-5.26z" />
                                                </svg>
                                                <!-- ORCID Icon -->
                                                <svg v-else-if="platform.icon === 'orcid'"
                                                    class="w-6 h-6 text-[#A6CE39]" fill="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path
                                                        d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zM7.369 4.378c.525 0 .947.431.947.947s-.422.947-.947.947a.948.948 0 0 1-.947-.947c0-.516.422-.947.947-.947zm-.722 3.038h1.444v10.041H6.647V7.416zm3.562 0h3.9c3.712 0 5.344 2.653 5.344 5.025 0 2.578-2.016 5.025-5.325 5.025h-3.919V7.416zm1.444 1.306v7.419h2.269c2.125 0 3.841-1.2 3.841-3.706 0-2.325-1.553-3.713-3.744-3.713H8.091z" />
                                                </svg>
                                                <!-- LinkedIn Icon -->
                                                <svg v-else-if="platform.icon === 'linkedin'"
                                                    class="w-6 h-6 text-[#0077b5]" fill="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path
                                                        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                                </svg>
                                                <!-- Scopus Icon -->
                                                <svg v-else-if="platform.icon === 'scopus'"
                                                    class="w-6 h-6 text-[#e9711c]" fill="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path
                                                        d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 21.75c-5.385 0-9.75-4.365-9.75-9.75S6.615 2.25 12 2.25s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z" />
                                                </svg>
                                                <!-- Default/Other Icon -->
                                                <svg v-else class="w-6 h-6 text-gray-400 group-hover:text-auas-600"
                                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                </svg>
                                            </div>
                                            <div>
                                                <h4
                                                    class="text-lg font-bold text-black group-hover:text-auas-600 transition-colors break-words">
                                                    {{
                                                        platform.platform }}</h4>
                                                <p class="text-sm text-gray-600">
                                                    {{ platform.safeUrl ? 'View Profile' : 'Profile link unavailable' }}
                                                </p>
                                            </div>
                                        </div>
                                    </component>
                                </div>
                                <p v-if="safeAcademicPlatforms.length === 0"
                                    class="text-gray-600 italic text-center py-8">No academic platform information
                                    available.</p>


                            </div>

                            <!-- CV Tab - Mobile Optimized -->
                            <div v-if="activeTab === 'cv'">
                                <h3 class="text-2xl font-bold text-black mb-4">Curriculum Vitae</h3>
                                <div v-if="staff.cv_file"
                                    class="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                                    <!-- Mobile-First CV Header -->
                                    <div class="p-4 sm:p-6">
                                        <div class="flex flex-col space-y-4">
                                            <!-- CV Info Section -->
                                            <div class="flex items-center space-x-3">
                                                <div
                                                    class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-auas-500 to-auas-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                                                    <svg class="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor"
                                                        viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd"
                                                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                                            clip-rule="evenodd" />
                                                    </svg>
                                                </div>
                                                <div class="min-w-0 flex-1">
                                                    <p class="font-medium text-black text-sm sm:text-base break-words">{{
                                                        staff.name_en || staff.name_ar ||
                                                        staff.name_ku }} - CV</p>
                                                    <p class="text-xs sm:text-sm text-gray-600">PDF Document</p>
                                                </div>
                                            </div>

                                            <!-- Mobile-Optimized Action Buttons -->
                                            <div class="grid grid-cols-2 gap-3">
                                                <button @click="downloadCV"
                                                    class="inline-flex items-center justify-center px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-all duration-300 text-sm font-medium">
                                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    Download
                                                </button>
                                                <a :href="getStaffCvUrl()" target="_blank"
                                                    class="inline-flex items-center justify-center px-4 py-3 bg-auas-500 hover:bg-auas-600 text-white rounded-xl transition-all duration-300 text-sm font-medium">
                                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                    Open
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- CV Loading State -->
                                    <div v-if="!cvLoaded && loadingCV" class="flex items-center justify-center py-20">
                                        <div class="text-center">
                                            <div
                                                class="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-xl rounded-2xl mb-4 border border-white/20">
                                                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white">
                                                </div>
                                            </div>
                                            <p class="text-black font-medium">Loading CV...</p>
                                            <p class="text-gray-600 text-sm mt-1">Please wait while we load the document
                                            </p>
                                            <div class="mt-4 w-64 bg-white/20 rounded-full h-2">
                                                <div class="bg-gradient-to-r from-auas-500 to-auas-600 h-2 rounded-full animate-pulse"
                                                    style="width: 60%"></div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- CV Error State -->
                                    <div v-if="cvError" class="flex items-center justify-center py-20">
                                        <div class="text-center">
                                            <div
                                                class="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-2xl mb-4 border border-red-500/30">
                                                <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                </svg>
                                            </div>
                                            <p class="text-black font-medium">Failed to Load CV</p>
                                            <p class="text-gray-600 text-sm mt-1">The document could not be loaded.
                                                Please try again or download
                                                directly.</p>
                                            <div
                                                class="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3 mt-4">
                                                <button @click="retryLoadCV"
                                                    class="inline-flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
                                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15">
                                                        </path>
                                                    </svg>
                                                    Retry Loading
                                                </button>
                                                <button @click="downloadCV"
                                                    class="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors">
                                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                                        </path>
                                                    </svg>
                                                    Download CV
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- CV Viewer - Mobile Responsive -->
                                    <div v-show="cvLoaded || (!loadingCV && !cvError)" class="w-full">
                                        <div
                                            class="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
                                            <PdfViewer v-if="shouldLoadCV && !cvError"
                                                :pdf-url="getStaffCvUrl()"
                                                :title="`${staff.name_en || staff.name_ar || staff.name_ku} - CV`"
                                                :height="pdfHeight" @loaded="onCVLoaded" @error="onCVError"
                                                @mount-error="onCVError" />
                                        </div>

                                        <!-- Load CV Button (if not auto-loaded) -->
                                        <div v-if="!shouldLoadCV" class="text-center py-12">
                                            <div
                                                class="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-4 border border-gray-200">
                                                <svg class="w-8 h-8 text-gray-600" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                                    </path>
                                                </svg>
                                            </div>
                                            <h4 class="text-lg font-semibold text-black mb-2">CV Preview</h4>
                                            <p class="text-gray-600 mb-4">Click below to load and preview the CV
                                                document</p>
                                            <button @click="loadCV"
                                                class="inline-flex items-center px-6 py-3 bg-gradient-to-r from-auas-600 to-auas-700 text-white rounded-2xl hover:from-auas-700 hover:to-auas-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                                                    </path>
                                                </svg>
                                                <span class="text-white">Load CV Preview</span>
                                            </button>
                                        </div>
                                    </div>

                                </div>
                                <p v-else class="text-gray-600 italic">No CV available for download.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>



        <br>
        <!-- <br> -->
    </div>
    <!-- <br>
<br> -->
</template>

<script setup>
import { ref, computed, h, onMounted, watch } from 'vue'
import { Link } from '@inertiajs/vue3'
import { route } from '@/utils/route'
import PdfViewer from '@/components/PdfViewer.vue'
import StaffPhoto from '@/components/StaffPhoto.vue'
import axios from 'axios'

const props = defineProps({
    staff: Object
})

const activeTab = ref('personal_info')

// Window width for responsive design
const windowWidth = ref(1024) // Default to desktop width

// Computed PDF height based on window width
const pdfHeight = computed(() => {
    return windowWidth.value < 640 ? '400px' : '600px'
})

// Helper function for safe JSON parsing
const safeJsonParse = (jsonString, defaultValue = []) => {
    if (!jsonString) return defaultValue
    try {
        return typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString
    } catch (e) {
        console.warn('Failed to parse JSON:', e)
        return defaultValue
    }
}

const validExternalUrl = (value) => {
    if (!value || typeof value !== 'string') return ''

    try {
        const url = new URL(value)
        return ['http:', 'https:'].includes(url.protocol) ? url.href : ''
    } catch {
        return ''
    }
}

const safeAcademicPlatforms = computed(() => {
    const platforms = safeJsonParse(props.staff?.academic_platforms)

    if (!Array.isArray(platforms)) return []

    return platforms
        .filter((platform) => platform && (platform.platform || platform.url))
        .map((platform) => ({
            ...platform,
            safeUrl: validExternalUrl(platform.url)
        }))
})

const normalizePublicationItems = (items) => {
    const list = safeJsonParse(items)

    return list
        .filter((publication) => publication && publication.title)
        .map((publication) => ({
            title: publication.title,
            authors: publication.authors || null,
            publication: publication.publication || publication.journal || null,
            citations: Number(publication.citations || publication.cited_by || 0),
            year: publication.year || null,
            type: publication.type || null,
            link: publication.link || publication.url || null,
        }))
}

// Publications data
const publications = ref(normalizePublicationItems(props.staff?.research_publications))
const loadingPublications = ref(false)
const publicationsError = ref(null)
const publicationsMessage = ref(publications.value.length > 0 ? 'Loaded from saved staff profile data.' : null)
const publicationsSource = ref(publications.value.length > 0 ? 'staff_profile' : null)

// Collapsible sections for Academic Activities
const expandedSections = ref({
    conferences: false,
    workshops: false,
    trainings: false
})

// Pagination for publications
const currentPage = ref(1)
const perPage = ref(5)
const perPageOptions = [5, 10, 15, 20, 25, 50, 100, 500]

// Pagination for teaching subjects
const teachingCurrentPage = ref(1)
const teachingPerPage = ref(5)
const teachingPerPageOptions = [5, 10, 15, 20, 25, 50]

// Pagination for supervisions
const supervisionCurrentPage = ref(1)
const supervisionPerPage = ref(5)
const supervisionPerPageOptions = [5, 10, 15, 20, 25, 50]

const paginatedSupervisions = computed(() => {
    if (!props.staff.supervisions) return []
    const start = (supervisionCurrentPage.value - 1) * supervisionPerPage.value
    const end = start + supervisionPerPage.value
    return props.staff.supervisions.slice(start, end)
})

const supervisionTotalPages = computed(() => {
    if (!props.staff.supervisions) return 0
    return Math.ceil(props.staff.supervisions.length / supervisionPerPage.value)
})

const changeSupervisionPage = (page) => {
    supervisionCurrentPage.value = page
}

const changeSupervisionPerPage = (value) => {
    supervisionPerPage.value = value
    supervisionCurrentPage.value = 1 // Reset to first page
}

const paginatedTeachingSubjects = computed(() => {
    if (!props.staff.teaching_subjects) return []
    const start = (teachingCurrentPage.value - 1) * teachingPerPage.value
    const end = start + teachingPerPage.value
    return props.staff.teaching_subjects.slice(start, end)
})

const teachingTotalPages = computed(() => {
    if (!props.staff.teaching_subjects) return 0
    return Math.ceil(props.staff.teaching_subjects.length / teachingPerPage.value)
})

const changeTeachingPage = (page) => {
    teachingCurrentPage.value = page
    // Scroll to top of teaching section
    const teachingSection = document.querySelector('#teaching-subjects')
    if (teachingSection) {
        teachingSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
}

const changeTeachingPerPage = (value) => {
    teachingPerPage.value = value
    teachingCurrentPage.value = 1 // Reset to first page
}

const paginatedPublications = computed(() => {
    const start = (currentPage.value - 1) * perPage.value
    const end = start + perPage.value
    return publications.value.slice(start, end)
})

const totalPages = computed(() => {
    return Math.ceil(publications.value.length / perPage.value)
})

const changePage = (page) => {
    currentPage.value = page
    // Scroll to top of publications section
    const researchSection = document.querySelector('#research-publications')
    if (researchSection) {
        researchSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
}

const changePerPage = (value) => {
    perPage.value = value
    currentPage.value = 1 // Reset to first page
}

// Toggle collapsible sections
const toggleSection = (section) => {
    expandedSections.value[section] = !expandedSections.value[section]
}

const parsedAcademicProfiles = computed(() => {
    const profiles = safeJsonParse(props.staff.academic_profiles)

    if (!Array.isArray(profiles)) return []

    return profiles
        .filter((profile) => profile && (profile.platform || profile.url))
        .map((profile) => ({
            ...profile,
            url: validExternalUrl(profile.url)
        }))
})
const scholarProfile = computed(() => parsedAcademicProfiles.value.find(p => p.platform === 'Google Scholar'))
const orcidProfile = computed(() => parsedAcademicProfiles.value.find(p => p.platform === 'ORCID'))

// Check for external publication profile availability.
const hasExternalPublicationSource = computed(() => {
    // Check direct accessor first
    if (validExternalUrl(props.staff?.google_scholar_url)) return true
    if (scholarProfile.value?.url || orcidProfile.value?.url) return true
    // Fallback to academic_platforms
    return safeAcademicPlatforms.value.some(p => ['Google Scholar', 'ORCID'].includes(p.platform) && p.safeUrl)
})

// Fetch publications from saved data, ORCID, then Google Scholar fallback.
const fetchPublications = async (force = false) => {
    if (!props.staff?.id) {
        return
    }

    if (!force && publications.value.length > 0) {
        return
    }

    loadingPublications.value = true
    publicationsError.value = null
    publicationsMessage.value = null
    currentPage.value = 1 // Reset to first page

    try {
        // Use the new submission endpoint
        const response = await axios.get(`/api/submission/${props.staff.id}/publications`)

        if (response.data.success) {
            publications.value = normalizePublicationItems(response.data.data)
            publicationsMessage.value = response.data.message || null
            publicationsSource.value = response.data.source || null
        } else {
            publications.value = []
            publicationsMessage.value = response.data.message || 'No publications found'
        }
    } catch (error) {
        console.error('Error fetching publications:', error)
        publicationsError.value = 'Unable to load publications at this time'
        publications.value = []
    } finally {
        loadingPublications.value = false
    }
}

// Fetch publications when research tab is opened
watch(activeTab, (newTab) => {
    if (newTab === 'research' && publications.value.length === 0 && !loadingPublications.value) {
        fetchPublications()
    }
})

// Fetch on mount if research tab is initially active
onMounted(() => {
    if (activeTab.value === 'research') {
        fetchPublications()
    }

    // Set initial window width
    if (typeof window !== 'undefined') {
        windowWidth.value = window.innerWidth

        // Add resize listener
        const handleResize = () => {
            windowWidth.value = window.innerWidth
        }

        window.addEventListener('resize', handleResize)

        // Cleanup on unmount
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }
})

// Share functionality
const showShareModal = ref(false)
const canShare = ref(false)

// CV loading functionality
const shouldLoadCV = ref(false)
const loadingCV = ref(false)
const cvLoaded = ref(false)
const cvError = ref(false)

// Check if native sharing is available
if (typeof navigator !== 'undefined' && navigator.share) {
    canShare.value = true
}

// Computed properties
const currentUrl = computed(() => {
    if (typeof window !== 'undefined') {
        return window.location.href
    }
    return ''
})

const tabs = computed(() => {
    const baseTabs = [
        { id: 'personal_info', name: 'Personal Information' },
        { id: 'biography', name: 'Biography' },
        { id: 'education', name: 'Education' },
        { id: 'scientific_titles', name: 'Scientific Titles' },
        { id: 'teaching', name: 'Teaching' },
        { id: 'research', name: 'Research' },
        { id: 'academic_activities', name: 'Academic Activities' },
        { id: 'supervision', name: 'Academic Supervising' },
        // { id: 'memberships', name: 'Memberships' },
        { id: 'academic_profiles', name: 'Academic Platforms' }
    ]

    if (props.staff.cv_file) {
        baseTabs.push({ id: 'cv', name: 'CV' })
    }

    return baseTabs
})

// Tab icon mapping - returns SVG icon component
const getTabIcon = (tabId) => {
    const iconComponents = {
        personal_info: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            })
        ]),
        biography: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
            })
        ]),
        education: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M12 14l9-5-9-5-9 5 9 5z'
            }),
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z'
            })
        ]),
        scientific_titles: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z'
            })
        ]),
        teaching: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
            })
        ]),
        research: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
            })
        ]),
        academic_profiles: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
            })
        ]),
        academic_activities: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
            })
        ]),
        supervision: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m3 5.197v1a6 6 0 01-3 5.197'
            })
        ]),
        memberships: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
            })
        ]),
        academic_profiles: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9h12'
            })
        ]),
        cv: () => h('svg', {
            class: 'w-4 h-4',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
        }, [
            h('path', {
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                'stroke-width': '2',
                d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            })
        ])
    }
    return iconComponents[tabId] || (() => h('div', { class: 'w-4 h-4' }))
}

// Get current tab name
const getCurrentTabName = () => {
    const currentTab = tabs.value.find(tab => tab.id === activeTab.value)
    return currentTab ? currentTab.name : 'Profile'
}

// Get current tab description
const getCurrentTabDescription = () => {
    const descriptions = {
        personal_info: 'Contact details and personal information',
        biography: 'Professional background and expertise overview',
        education: 'Academic qualifications and educational background',
        scientific_titles: 'Academic titles and professional designations',
        teaching: 'Teaching experience and course responsibilities',
        research: 'Research publications and academic contributions',
        academic_activities: 'Conference presentations, workshops, and training participation',
        supervision: 'Student supervision and mentoring activities',
        memberships: 'Professional associations and memberships',
        academic_profiles: 'Academic social networks and profile links',
        cv: 'Complete curriculum vitae document'
    }
    return descriptions[activeTab.value] || 'Academic profile information'
}

const getStaffDepartmentName = () => {
    return props.staff.department_lookup?.name_en || props.staff.department || 'No department'
}

const getStaffCvUrl = () => {
    if (props.staff.cv_url) {
        return props.staff.cv_url
    }

    if (!props.staff.cv_file) {
        return ''
    }

    return props.staff.cv_file.startsWith('http') || props.staff.cv_file.startsWith('/')
        ? props.staff.cv_file
        : `/storage/${props.staff.cv_file}`
}

// Share functionality
const shareProfile = () => {
    showShareModal.value = true
    // Prevent body scroll when modal is open
    if (typeof document !== 'undefined') {
        document.body.style.overflow = 'hidden'
    }
}

const closeShareModal = () => {
    showShareModal.value = false
    // Restore body scroll
    if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
    }
}

const copyToClipboard = async (event) => {
    try {
        const url = window.location.href
        await navigator.clipboard.writeText(url)

        // Show success feedback
        const button = event.target.closest('button')
        const originalText = button.querySelector('p').textContent
        button.querySelector('p').textContent = 'Copied!'
        button.classList.add('bg-green-100')

        setTimeout(() => {
            button.querySelector('p').textContent = originalText
            button.classList.remove('bg-green-100')
        }, 2000)

    } catch (err) {
        console.error('Failed to copy: ', err)
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = window.location.href
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try {
            document.execCommand('copy')
        } catch (copyErr) {
            console.error('Fallback copy failed:', copyErr)
        }
        document.body.removeChild(textArea)

        // Still show success feedback for fallback
        const button = event.target.closest('button')
        const originalText = button.querySelector('p').textContent
        button.querySelector('p').textContent = 'Copied!'
        button.classList.add('bg-green-100')

        setTimeout(() => {
            button.querySelector('p').textContent = originalText
            button.classList.remove('bg-green-100')
        }, 2000)
    }
}

const shareViaEmail = () => {
    const subject = encodeURIComponent(`Academic Profile: ${props.staff.name_en}`)
    const body = encodeURIComponent(`Check out this academic profile:

${props.staff.name_en}
${props.staff.title_en}
${getStaffDepartmentName()}

${window.location.href}`)
    window.open(`mailto:?subject=${subject}&body=${body}`)
    closeShareModal()
}

const shareViaNative = async () => {
    try {
        await navigator.share({
            title: `Academic Profile: ${props.staff.name_en}`,
            text: `${props.staff.name_en} - ${props.staff.title_en}`,
            url: window.location.href
        })
        closeShareModal()
    } catch (err) {
        console.error('Error sharing:', err)
    }
}

// CV functionality
let loadingTimeout = null

const loadCV = async () => {
    // Check if CV file exists
    if (!props.staff.cv_file) {
        console.warn('No CV file available')
        onCVError('No CV file available')
        return
    }

    // Check if the file is accessible
    const cvUrl = getStaffCvUrl()
    console.log('Attempting to load CV from:', cvUrl)

    try {
        // Test if the file is accessible
        const response = await fetch(cvUrl, { method: 'HEAD' })
        if (!response.ok) {
            console.error('CV file not accessible:', response.status, response.statusText)
            onCVError(`CV file not accessible (${response.status})`)
            return
        }
        console.log('CV file is accessible, proceeding with PDF viewer')
    } catch (error) {
        console.error('Error checking CV file accessibility:', error)
        onCVError('CV file not accessible - network error')
        return
    }

    shouldLoadCV.value = true
    loadingCV.value = true
    cvError.value = false
    cvLoaded.value = false

    // Set a timeout to prevent infinite loading
    if (loadingTimeout) {
        clearTimeout(loadingTimeout)
    }

    loadingTimeout = setTimeout(() => {
        if (loadingCV.value && !cvLoaded.value) {
            console.warn('PDF loading timeout - switching to error state')
            onCVError('PDF loading timeout - the document may be too large or corrupted')
        }
    }, 10000) // 10 seconds to allow PDF to load properly
}

const onCVLoaded = () => {
    if (loadingTimeout) {
        clearTimeout(loadingTimeout)
        loadingTimeout = null
    }
    cvLoaded.value = true
    loadingCV.value = false
    console.log('PDF loaded successfully')
}

const onCVError = (error) => {
    if (loadingTimeout) {
        clearTimeout(loadingTimeout)
        loadingTimeout = null
    }
    console.error('PDF loading error:', error)
    cvError.value = true
    loadingCV.value = false
    cvLoaded.value = false

    // Show download fallback immediately
    showDownloadFallback.value = true

    // Handle retry logic
    handleCVLoadFailure()

    // Show user-friendly error message
    if (error && error.includes('timeout')) {
        console.warn('CV loading timed out - document may be too large or corrupted')
    } else {
        console.warn('CV loading failed - document may not be accessible')
    }
}

const retryLoadCV = () => {
    cvError.value = false
    cvLoaded.value = false
    shouldLoadCV.value = false

    // Small delay before retrying
    setTimeout(() => {
        loadCV()
    }, 500)
}

// Auto-fallback to download if PDF viewer fails multiple times
let retryCount = 0
const maxRetries = 2

const handleCVLoadFailure = () => {
    retryCount++
    if (retryCount >= maxRetries) {
        console.warn('PDF viewer failed multiple times - offering direct download')
        // Show a notification that we're falling back to download
        setTimeout(() => {
            console.log('Auto-triggering CV download as fallback')
            downloadCV()
        }, 1000)
    }
}

// Alternative: Show download button immediately if PDF viewer fails
const showDownloadFallback = ref(false)

const downloadCV = () => {
    const cvUrl = getStaffCvUrl()
    if (!cvUrl) {
        return
    }

    const link = document.createElement('a')
    link.href = cvUrl
    link.download = `${props.staff.name_en}_CV.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}
</script>

<style scoped>
.prose {
    color: #000000;
    line-height: 1.625;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
    color: rgb(var(--color-academic-900));
    font-weight: 600;
}

.prose p {
    margin-bottom: 1rem;
}

.prose ul,
.prose ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
}

.prose li {
    margin-bottom: 0.5rem;
}

/* Academic Professional Animations */
@keyframes fade-in {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fade-in-up {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slide-in-left {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes pulse-glow {

    0%,
    100% {
        box-shadow: 0 0 5px rgb(var(--color-primary-500) / 0.3);
    }

    50% {
        box-shadow: 0 0 20px rgb(var(--color-primary-500) / 0.6);
    }
}

.animate-fade-in {
    animation: fade-in 0.6s ease-out;
}

.animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out;
    animation-fill-mode: both;
}

.animate-slide-in-left {
    animation: slide-in-left 0.7s ease-out;
}

/* Academic Color Scheme */
.academic-gradient {
    background: linear-gradient(135deg, rgb(var(--color-primary-800)) 0%, rgb(var(--color-primary-700)) 50%, rgb(var(--color-primary-700)) 100%);
}

.academic-text {
    background: linear-gradient(135deg, rgb(var(--color-primary-800)), rgb(var(--color-primary-700)));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* Glassmorphism Effects */
.glass-card {
    background: rgb(255 255 255 / 0.85);
    backdrop-filter: blur(20px);
    border: 1px solid rgb(255 255 255 / 0.2);
    box-shadow: 0 8px 32px rgb(0 0 0 / 0.1);
}

/* Hover Effects */
.hover-lift {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-lift:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgb(0 0 0 / 0.15);
}

/* Tab Animations */
.tab-content {
    animation: fade-in 0.5s ease-out;
}

/* Responsive Design */
@media (max-width: 1024px) {
    .lg\:w-80 {
        width: 100%;
    }

    .lg\:flex-row {
        flex-direction: column;
    }
}

/* Professional Table Styling */
table {
    border-collapse: separate;
    border-spacing: 0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

thead th {
    background: linear-gradient(135deg, rgb(var(--color-academic-50)) 0%, rgb(var(--color-academic-200)) 100%);
    font-weight: 600;
    color: rgb(var(--color-academic-600));
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.75rem;
}

tbody tr {
    transition: all 0.2s ease;
}

tbody tr:hover {
    background-color: rgb(var(--color-academic-50));
    transform: scale(1.01);
}

/* Academic Professional Colors */
.text-academic-primary {
    color: rgb(var(--color-primary-800));
}

.text-academic-secondary {
    color: rgb(var(--color-primary-700));
}

.bg-academic-light {
    background-color: rgb(var(--color-academic-100));
}

.border-academic {
    border-color: rgb(var(--color-academic-300));
}

/* Animations */
@keyframes float {

    0%,
    100% {
        transform: translateY(0px) rotate(0deg);
    }

    33% {
        transform: translateY(-10px) rotate(1deg);
    }

    66% {
        transform: translateY(5px) rotate(-1deg);
    }
}

@keyframes float-delayed {

    0%,
    100% {
        transform: translateY(0px) rotate(0deg);
    }

    33% {
        transform: translateY(8px) rotate(-1deg);
    }

    66% {
        transform: translateY(-12px) rotate(1deg);
    }
}

@keyframes pulse-slow {

    0%,
    100% {
        opacity: 0.3;
    }

    50% {
        opacity: 0.8;
    }
}

@keyframes fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes fade-in-up {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-float {
    animation: float 6s ease-in-out infinite;
}

.animate-float-delayed {
    animation: float-delayed 8s ease-in-out infinite;
}

.animate-pulse-slow {
    animation: pulse-slow 4s ease-in-out infinite;
}

.animate-fade-in {
    animation: fade-in 0.6s ease-out;
}

.animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out;
}

/* Modal Animation */
@keyframes modal-in {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(-10px);
    }

    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@keyframes backdrop-in {
    from {
        opacity: 0;
        backdrop-filter: blur(0px);
    }

    to {
        opacity: 1;
        backdrop-filter: blur(16px);
    }
}

.animate-modal-in {
    animation: modal-in 0.3s ease-out;
}

.animate-backdrop-in {
    animation: backdrop-in 0.3s ease-out;
}
</style>
